import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Stack,
  Group,
  Text,
  NumberInput,
  TextInput,
  Box,
  Divider,
  Chip,
  Switch
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  DEFAULT_TRAVEL_TYPE,
  TRAVEL_STATUSES,
  TravelTypeValue,
} from "@/config";

import { ApiError } from "@/utils/apiResultHandler";
import { localStringToUtc, getCurrentLocalDateTimeString } from "@/utils/dateHelpers";

import { TravelInput, Travel } from "@/utils/api/TravelService";

import { usePlaceLocation, PlaceInitialValues } from "@/hooks/usePlaceLocation";

import { useDashboardContext } from "@/contexts/DashboardContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useActiveTravelContext } from "@/contexts/ActiveTravelContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { AppIcon } from "@/components/generic/AppIcon";

import { CarSelector } from "@/components/features/Cars/CarSelector";
import { PlaceLocationSection } from "@/components/features/PlaceLocationSection";
import { TravelTypeSelector } from "@/components/features/Travels/TravelTypeSelector";

// ============================================
// Types
// ============================================

export interface StartTravelDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Callback when drawer should close */
  onClose: () => void;

  /** Callback after travel is successfully started (e.g. to update active travel context) */
  onTravelStarted?: (travel: Travel) => void;

  /** Default car ID to pre-select */
  defaultCarId?: string | null;

  /** Default travel type */
  defaultTravelType?: TravelTypeValue;

  /**
   * Previous travel for the selected car to pre-populate starting location.
   * The lastRecord from this travel will be used to fill in:
   * - pointType (waypoint type like home, office, etc.)
   * - whereDone (place name)
   * - location (full address)
   * - odometer (starting odometer reading)
   *
   * When previousTravel is provided, auto-location is disabled.
   */
  previousTravel?: Travel | null;
}

interface FormState {
  carId: string | null;
  travelType: TravelTypeValue;
  odometer: number | null;
  startTime: string; // Local time string for DateTimePicker
  purpose: string; // Trip purpose for tax deduction documentation
  enableTracking: boolean;
}

// ============================================
// Constants
// ============================================

const KNOWN_FORM_FIELDS = [
  "carId",
  "travelType",
  "pointType",
  "odometer",
  "whereDone",
  "location",
  "latitude",
  "longitude",
  "startTime",
  "whenDone",
  "purpose",
  "savePlace",
  "savedPlaceId",
  "status",
  "firstRecord",
];

// ============================================
// Helper Functions
// ============================================

/**
 * Get initial form state from props.
 */
const getInitialFormState = (
  defaultCarId?: string | null,
  defaultTravelType?: TravelTypeValue,
  previousTravel?: Travel | null
): FormState => {
  return {
    carId: defaultCarId || null,
    travelType: (previousTravel?.travelType as TravelTypeValue) || defaultTravelType || DEFAULT_TRAVEL_TYPE,
    odometer: previousTravel?.lastRecord?.odometer ?? null,
    startTime: getCurrentLocalDateTimeString(),
    purpose: previousTravel?.purpose || "",
    enableTracking: false,
  };
};

/**
 * Build PlaceInitialValues from previous travel's last record.
 */
const getPlaceInitialValues = (
  previousTravel?: Travel | null
): PlaceInitialValues | undefined => {
  const lastRecord = previousTravel?.lastRecord;
  if (!lastRecord) return undefined;

  return {
    pointType: lastRecord.pointType || null,
    whereDone: lastRecord.whereDone || "",
    location: lastRecord.location || "",
    latitude: lastRecord.latitude ?? null,
    longitude: lastRecord.longitude ?? null,
    savedPlaceId: lastRecord.savedPlaceId ?? null,
    // Build addressData from last record's structured fields if available
    addressData: lastRecord.address1 ? {
      addressId: "",
      displayName: null,
      address1: lastRecord.address1 || null,
      address2: lastRecord.address2 || null,
      city: lastRecord.city || null,
      country: lastRecord.countryId || null,
      latitude: lastRecord.latitude ?? null,
      longitude: lastRecord.longitude ?? null,
      postalCode: lastRecord.postalCode || null,
      region: lastRecord.stateProvince || null,
    } : null,
  };
};

// ============================================
// Dirty-state serializer (stable key order)
// ============================================

const serializeFormSnapshot = (data: FormState): string => {
  return JSON.stringify(data);
};

// ============================================
// Component
// ============================================

const StartTravelDrawerComponent: React.FC<StartTravelDrawerProps> = ({
  opened,
  onClose,
  onTravelStarted,
  defaultCarId,
  defaultTravelType,
  previousTravel,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort } = useUserProfileContext();
  const { getCarById, totalCarsCount, items } = useDashboardContext();
  const { startTravel } = useActiveTravelContext();
  const { isMobile } = useAppEnvironment();

  // Determine if we have previous travel data to pre-populate
  const hasPreviousTravel = useMemo(() => {
    return !!(previousTravel?.lastRecord);
  }, [previousTravel]);

  // Form state (non-place fields only)
  const [formState, setFormState] = useState<FormState>(() =>
    getInitialFormState(defaultCarId, defaultTravelType, previousTravel)
  );

  // Place/location state via reusable hook
  const placeLocation = usePlaceLocation({
    enabled: opened,
    autoLocate: !hasPreviousTravel,
    initialValues: getPlaceInitialValues(previousTravel),
    travelType: formState.travelType,
    includePointType: true,
    onFieldChange: () => setPlaceDirty(true),
  });

  // Operation state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastError, setLastError] = useState<ApiError | null>(null);

  const recentTravelPurposes = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items[0]?.recentTravelPurposes || [];
  }, [items]);

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [placeDirty, setPlaceDirty] = useState(false);

  // Track dirty state reactively (form fields + place fields)
  useEffect(() => {
    if (!opened) return;
    const formChanged = serializeFormSnapshot(formState) !== initialSnapshotRef.current;
    setIsDirty(formChanged || placeDirty);
  }, [formState, placeDirty, opened]);

  // ============================================
  // Error Handling (from ApiError)
  // ============================================

  const { fields: fieldErrors, genericErrors } = useMemo(() => {
    return ApiError.getFormErrors(lastError, KNOWN_FORM_FIELDS);
  }, [lastError]);

  const formattedGenericError = useMemo(() => {
    return ApiError.formatErrors(genericErrors);
  }, [genericErrors]);

  // Reset form when drawer opens
  useEffect(() => {
    if (opened) {
      const newFormState = getInitialFormState(
        defaultCarId,
        defaultTravelType,
        previousTravel
      );

      setFormState(newFormState);

      // Capture initial snapshot for dirty tracking
      initialSnapshotRef.current = serializeFormSnapshot(newFormState);
      setIsDirty(false);
      setPlaceDirty(false);

      // Reset place location with values from previous travel (if any)
      placeLocation.reset(getPlaceInitialValues(previousTravel));

      setLastError(null);
    }
  }, [opened, defaultCarId, defaultTravelType, previousTravel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get selected car for settings
  const selectedCar = useMemo(() => {
    if (!formState.carId) return null;
    return getCarById(formState.carId);
  }, [formState.carId, getCarById]);

  // Get odometer unit from car settings
  const odometerUnit = useMemo(() => {
    return selectedCar?.mileageIn || distanceLabelShort;
  }, [selectedCar?.mileageIn, distanceLabelShort]);

  // ============================================
  // Handlers
  // ============================================

  const handleCarChange = useCallback(
    (_name: string, value: string | null) => {
      setFormState((prev) => ({ ...prev, carId: value }));
    },
    []
  );

  const handlePurposeSelect = useCallback((selectedPurpose: string) => {
    setFormState((prev) => ({
      ...prev,
      purpose: prev.purpose === selectedPurpose ? "" : selectedPurpose,
    }));
  }, []);

  const handleTravelTypeChange = useCallback(
    (_name: string, value: TravelTypeValue | null) => {
      setFormState((prev) => ({
        ...prev,
        travelType: value || DEFAULT_TRAVEL_TYPE,
      }));
    },
    []
  );

  const handleOdometerChange = useCallback((value: string | number) => {
    setFormState((prev) => ({
      ...prev,
      odometer: typeof value === "number" ? value : null,
    }));
  }, []);

  const handlePurposeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({
        ...prev,
        purpose: event.target.value,
      }));
    },
    []
  );

  const handleStartTimeChange = useCallback((value: string | null) => {
    setFormState((prev) => ({
      ...prev,
      startTime: value || getCurrentLocalDateTimeString(),
    }));
  }, []);

  const handleEnableTrackingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enableTracking = event.currentTarget?.checked;

      setFormState((prev) => ({
        ...prev,
        enableTracking
      }));
    },
    []
  );

  const handleStart = useCallback(async () => {
    if (!formState.carId) return;

    setLastError(null);
    setIsSubmitting(true);

    const placeFields = placeLocation.getPlaceFields();

    const params: TravelInput = {
      carId: formState.carId,
      travelType: formState.travelType,
      status: TRAVEL_STATUSES.IN_PROGRESS,
      purpose: formState.purpose || undefined,
      enableTracking: formState.enableTracking || undefined,
      savePlace: placeFields.savePlace || undefined,
      firstRecord: {
        carId: formState.carId,
        whenDone: localStringToUtc(formState.startTime),
        odometer: formState.odometer || undefined,
        pointType: placeFields.pointType || undefined,
        whereDone: placeFields.whereDone || undefined,
        location: placeFields.location || undefined,
        address1: placeFields.address1 || undefined,
        address2: placeFields.address2 || undefined,
        city: placeFields.city || undefined,
        stateProvince: placeFields.stateProvince || undefined,
        postalCode: placeFields.postalCode || undefined,
        countryId: placeFields.countryId || undefined,
        latitude: placeFields.latitude ?? undefined,
        longitude: placeFields.longitude ?? undefined,
        savedPlaceId: placeFields.savedPlaceId || undefined,
      },
    };

    try {
      const result = await startTravel(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newTravel = (result.data as Travel[])[0];

        setIsDirty(false);
        onClose();

        if (newTravel) {
          onTravelStarted?.(newTravel);
        }
      } else {
        setLastError(ApiError.fromResult(result, "Failed to start travel"));
      }
    } catch (error) {
      setLastError(ApiError.fromException(error, "Failed to start travel"));
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, placeLocation, startTravel, onClose, onTravelStarted]);

  // ============================================
  // Validation
  // ============================================

  const canStart = useMemo(() => {
    return !!formState.carId && !isSubmitting;
  }, [formState.carId, isSubmitting]);

  // Determine if we pre-populated from previous travel
  const hasPrePopulatedData = useMemo(() => {
    const lastRecord = previousTravel?.lastRecord;
    return !!(
      lastRecord?.pointType ||
      lastRecord?.whereDone ||
      lastRecord?.location ||
      lastRecord?.odometer
    );
  }, [previousTravel]);

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("travels:drawers.start.title")}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={85}
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      actionType="save-cancel"
      onConfirm={handleStart}
      confirmLabel={t("travels:drawers.start.actions.start")}
      confirmIcon="PlayerPlay"
      confirmDisabled={!canStart}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {/* Start Time */}
        <DateTimePicker
          label={t("travels:drawers.start.fields.startTime.label")}
          value={formState.startTime}
          onChange={handleStartTimeChange}
          error={fieldErrors.startTime || fieldErrors.whenDone}
          maxDate={new Date()}
          size="md"
          clearable={false}
          valueFormat="MMM DD, YYYY HH:mm"
          dropdownType={isMobile ? "modal" : "popover"}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: true },
            format: "12h",
          }}
        />

        {/* Vehicle Selector */}
        <CarSelector
          name="carId"
          value={formState.carId}
          onChange={handleCarChange}
          error={fieldErrors.carId}
          required
          placeholder={t("travels:drawers.start.fields.car.placeholder")}
          disabled={totalCarsCount === 1}
          withinPortal
        />

        {/* Travel Type Selector */}
        <TravelTypeSelector
          name="travelType"
          value={formState.travelType}
          onChange={handleTravelTypeChange}
          required
        />

        {/* Purpose (optional - for tax deduction documentation) */}
        <Box>
          <TextInput
            label={t("travels:drawers.start.fields.purpose.label")}
            description={t("travels:drawers.start.fields.purpose.description")}
            placeholder={t("travels:drawers.start.fields.purpose.placeholder")}
            value={formState.purpose}
            onChange={handlePurposeChange}
            error={fieldErrors.purpose}
            size="md"
            maxLength={200}
          />

          {recentTravelPurposes.length > 0 && (
            <Box
              style={
                isMobile
                  ? {
                    display: "flex",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    gap: "var(--mantine-spacing-xs)",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    paddingBottom: 2,
                  }
                  : {
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--mantine-spacing-xs)",
                  }
              }
            >
              {recentTravelPurposes.map((item) => (
                <Chip
                  key={item.purpose}
                  checked={formState.purpose === item.purpose}
                  onChange={() => handlePurposeSelect(item.purpose)}
                  size="xs"
                  variant="outline"
                  styles={{
                    label: {
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      height: 36,
                      paddingLeft: 14,
                      paddingRight: 14,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                >
                  {item.purpose}
                </Chip>
              ))}
            </Box>
          )}
        </Box>

        {/* Live GPS Tracking Toggle */}
        <Box
          p="sm"
          style={{
            backgroundColor: "var(--mantine-color-blue-0)",
            borderRadius: "var(--mantine-radius-md)",
            border: formState.enableTracking
              ? "1px solid var(--mantine-color-blue-3)"
              : "1px solid var(--mantine-color-blue-1)",
            transition: "border-color 200ms ease",
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <AppIcon
                iconName="MapPinPlus"
                size={20}
                color={
                  formState.enableTracking
                    ? "var(--mantine-color-blue-6)"
                    : "var(--mantine-color-gray-5)"
                }
                style={{ flexShrink: 0 }}
              />
              <Box>
                <Text size="sm" fw={500} c={formState.enableTracking ? "blue.7" : "neutral.7"}>
                  {t("travels:drawers.start.fields.enableTracking.label", {
                    defaultValue: "Live GPS tracking",
                  })}
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                  {t("travels:drawers.start.fields.enableTracking.description", {
                    defaultValue: "Automatically record your route while driving. The app must be open and active to track your driving",
                  })}
                </Text>
              </Box>
            </Group>
            <Switch
              checked={formState.enableTracking}
              onChange={handleEnableTrackingChange}
              size="md"
              color="blue"
            />
          </Group>
        </Box>

        <Divider
          label={t("travels:drawers.start.sections.startingPoint")}
          labelPosition="center"
        />

        {/* Pre-populated hint (when using previous travel data) */}
        {hasPrePopulatedData && (
          <Box
            p="xs"
            style={{
              backgroundColor: "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-sm)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
          >
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <AppIcon
                iconName="History"
                size={16}
                color="var(--mantine-color-blue-6)"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="xs" c="blue.7">
                {t("travels:drawers.start.hints.prePopulated")}
              </Text>
            </Group>
          </Box>
        )}

        {/* Place, Address, Map Preview, Save Place Toggle */}
        <PlaceLocationSection
          placeLocation={placeLocation}
          showPointType
          fieldErrors={fieldErrors}
          pointTypeLabel={t("travels:drawers.start.fields.pointType.label")}
          placeNameLabel={t("travels:drawers.start.fields.whereDone.label")}
          placeNamePlaceholder={t("travels:drawers.start.fields.whereDone.placeholder")}
          addressLabel={t("travels:drawers.start.fields.location.label")}
          addressPlaceholder={t("travels:drawers.start.fields.location.placeholder")}
        />

        {/* Starting Odometer (Optional) */}
        <NumberInput
          label={t("travels:drawers.start.fields.odometer.label")}
          description={t("travels:drawers.start.fields.odometer.description")}
          placeholder={t("travels:drawers.start.fields.odometer.placeholder")}
          value={formState.odometer ?? ""}
          onChange={handleOdometerChange}
          error={fieldErrors.odometer}
          min={0}
          max={9999999}
          rightSection={
            <Text size="sm" c="dimmed" pr="sm">
              {t(`common:${odometerUnit}`)}
            </Text>
          }
          rightSectionWidth={50}
          size="md"
          hideControls
        />

        {/* Info text about odometer mode */}
        <Box
          p="sm"
          style={{
            backgroundColor: "var(--mantine-color-gray-0)",
            borderRadius: "var(--mantine-radius-sm)",
          }}
        >
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <AppIcon
              iconName="InfoCircle"
              size={18}
              color="var(--mantine-color-gray-6)"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Text size="sm" c="dimmed">
              {formState.odometer
                ? t("travels:drawers.start.hints.odometerMode")
                : t("travels:drawers.start.hints.manualMode")}
            </Text>
          </Group>
        </Box>
      </Stack>
    </AppDrawer>
  );
};

export const StartTravelDrawer = React.memo(StartTravelDrawerComponent);